import type { Activity, ActivityEvent, ActivityRecord } from './activity.js'

/**
 * Elapsed/timer/moving time and pause computation (`activity-timestamps`
 * spec: "Elapsed time, timer time, and moving time are kept distinct" and
 * "Absence of GPS movement is not treated as an explicit pause"). Pauses
 * are derived *only* from explicit timer `event` messages — this function
 * never inspects GPS/speed data, so the "no GPS movement != paused" rule
 * holds by construction rather than needing a special case.
 *
 * Pause semantics: a pause is a timer stop followed by a timer start
 * (`[stop, start]`). A final stop with no later start is the end of the
 * activity and is NOT a pause, unless records continue after it, in
 * which case the pause runs from the stop to the last record.
 * `calc/moving.ts`'s `timerStoppedWindows` uses the same stop/start walk
 * (it keeps the trailing window open-ended, which excludes the same
 * intervals: there are no records after it unless it is a pause here).
 */

export interface Pause {
  start: Date
  end: Date
}

export interface TimingResult {
  /** Wall-clock span from the first to the last timestamped message, in seconds. */
  elapsedTimeSec: number
  /** Sum of running (non-paused) timer intervals, in seconds. */
  timerTimeSec: number
  pauses: Pause[]
}

const TIMER_STOP_EVENT_TYPES = new Set(['stop', 'stopAll'])
const TIMER_START_EVENT_TYPES = new Set(['start'])

function allTimestamps(records: ActivityRecord[], events: ActivityEvent[]): Date[] {
  const timestamps: Date[] = []
  for (const record of records) {
    if (record.timestamp instanceof Date) timestamps.push(record.timestamp)
  }
  for (const event of events) {
    if (event.timestamp instanceof Date) timestamps.push(event.timestamp)
  }
  return timestamps
}

function lastRecordTime(records: ActivityRecord[]): number | undefined {
  let last: number | undefined
  for (const record of records) {
    if (!(record.timestamp instanceof Date)) continue
    const t = record.timestamp.getTime()
    if (last == null || t > last) last = t
  }
  return last
}

/**
 * Whether the timer is running after applying `events` in timestamp
 * order, with exactly the walker semantics `computeTimingFromRecordsAndEvents`
 * uses (initially running; `stop`/`stopAll` stops a running timer;
 * `start` restarts a stopped one; anything else is ignored). Events must
 * already be in timestamp order (ties keep array order).
 */
export function timerRunningAfter(events: ActivityEvent[]): boolean {
  let isRunning = true
  for (const event of events) {
    if (event.event !== 'timer' || !(event.timestamp instanceof Date)) continue
    const eventType = typeof event.eventType === 'string' ? event.eventType : undefined
    if (eventType == null) continue
    if (isRunning && TIMER_STOP_EVENT_TYPES.has(eventType)) isRunning = false
    else if (!isRunning && TIMER_START_EVENT_TYPES.has(eventType)) isRunning = true
  }
  return isRunning
}

/**
 * The same computation `computeTiming` does for a whole `Activity`, but
 * scoped to an arbitrary records/events subset — used by
 * `calc/boundaries.ts` to recompute a single lap's or session's timing
 * after an edit changes which records it actually contains.
 */
export function computeTimingFromRecordsAndEvents(
  records: ActivityRecord[],
  events: ActivityEvent[],
): TimingResult {
  const timestamps = allTimestamps(records, events)
  if (timestamps.length === 0) {
    return { elapsedTimeSec: 0, timerTimeSec: 0, pauses: [] }
  }

  const sorted = [...timestamps].sort((a, b) => a.getTime() - b.getTime())
  const first = sorted[0]
  const last = sorted[sorted.length - 1]
  if (first == null || last == null) {
    return { elapsedTimeSec: 0, timerTimeSec: 0, pauses: [] }
  }
  const elapsedTimeSec = (last.getTime() - first.getTime()) / 1000

  const timerEvents = events
    .filter((e) => e.event === 'timer' && e.timestamp instanceof Date)
    .sort((a, b) => (a.timestamp as Date).getTime() - (b.timestamp as Date).getTime())

  const pauses: Pause[] = []
  let timerTimeSec = 0
  let runningSince: Date = first
  let isRunning = true

  for (const event of timerEvents) {
    const timestamp = event.timestamp as Date
    const eventType = typeof event.eventType === 'string' ? event.eventType : undefined

    if (isRunning && eventType != null && TIMER_STOP_EVENT_TYPES.has(eventType)) {
      timerTimeSec += (timestamp.getTime() - runningSince.getTime()) / 1000
      pauses.push({ start: timestamp, end: timestamp })
      isRunning = false
    } else if (!isRunning && eventType != null && TIMER_START_EVENT_TYPES.has(eventType)) {
      const lastPause = pauses[pauses.length - 1]
      if (lastPause != null) lastPause.end = timestamp
      runningSince = timestamp
      isRunning = true
    }
  }

  if (isRunning) {
    timerTimeSec += (last.getTime() - runningSince.getTime()) / 1000
  } else {
    // The timer was stopped and never restarted. A stop with no later
    // record is the normal end of the activity (Garmin devices write a
    // final `stop`/`stopAll`), NOT a pause — counting it made a one-pause
    // activity show "Pauses 2". Only when records continue after the
    // final stop (the device kept writing data while stopped) is that
    // stopped stretch a pause, open until the last record.
    const trailing = pauses[pauses.length - 1]
    const lastRecordMs = lastRecordTime(records)
    if (trailing != null) {
      if (lastRecordMs != null && lastRecordMs > trailing.start.getTime()) {
        trailing.end = new Date(lastRecordMs)
      } else {
        pauses.pop()
      }
    }
  }

  // No timer events at all: timer time equals elapsed time (never paused).
  if (timerEvents.length === 0) {
    timerTimeSec = elapsedTimeSec
  }

  return { elapsedTimeSec, timerTimeSec, pauses }
}

export function computeTiming(activity: Activity): TimingResult {
  return computeTimingFromRecordsAndEvents(activity.records, activity.events)
}
